import React, { useState, useEffect } from 'react';
import { Button, Card, CardBody, CardHeader, Table } from 'reactstrap';
import _ from 'lodash';
import { DayOfWeek } from '../constants/index';
import NewBusinessHourModal from './NewBusinessHourModal';
import EditBusinessHourModal from './EditBusinessHourModal';
import api from '../services/api';
import Layout from './Layout';

const BusinessHours = () => {
    const [businessHours, setBusinessHours] = useState({});
    const [newBusinessHours, setNewBusinessHours] = useState({
        organization: process.env.REACT_APP_CURRENT_ORGANIZATION_ID,
        start_time: '',
        end_time: '',
        timeframe: '',
        day_of_week: '',
    });
    const [editBusinessHours, setEditBusinessHours] = useState(null);
    const [newModalOpen, setNewModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [timeframes, setTimeframes] = useState([]);

    useEffect(() => {
        const fetchTimeframes = async () => {
            try {
                const response = await api.get('/timeframes/');
                setTimeframes(response.data);
            } catch (error) {
                console.error('Error fetching timeframes:', error);
            }
        };
        fetchTimeframes();

        fetchBusinessHours();
    }, []);

    const fetchBusinessHours = async () => {
        try {
            const response = await api.get('/all-business-hours/');
            const groupedBySet = _.groupBy(response.data, 'timeframeDetails.name');
            setBusinessHours(groupedBySet);
        } catch (error) {
            console.error('Error fetching business hours:', error);
        }
    };

    const appendSeconds = (time) => {
        return time + ':00';
    };

    const addBusinessHours = async () => {
        try {
            await api.post('/all-business-hours/', {
                ...newBusinessHours,
                timeframe: newBusinessHours.timeframe,
                start_time: appendSeconds(newBusinessHours.start_time),
                end_time: appendSeconds(newBusinessHours.end_time)
            });
            fetchBusinessHours();

            // Reset the form and close the modal
            setNewBusinessHours({
                organization: process.env.REACT_APP_CURRENT_ORGANIZATION_ID,
                start_time: '',
                end_time: '',
                timeframe: '',
                day_of_week: '',
            });

            setNewModalOpen(false);
        } catch (error) {
            console.error('Error adding business hours:', error);
        }
    };

    const deleteBusinessHours = async (id) => {
        try {
            await api.delete(`/all-business-hours/${id}`);
            fetchBusinessHours();
        } catch (error) {
            console.error('Error deleting business hours:', error);
        }
    };

    const openEditModal = (hours) => {
        setEditBusinessHours(hours);
        setEditModalOpen(true);
    };

    const editBusinessHoursData = async (updatedBusinessHours) => {
        try {
            await api.put(`/all-business-hours/${updatedBusinessHours.id}/`, updatedBusinessHours);
            fetchBusinessHours();
            setEditModalOpen(false);
        } catch (error) {
            console.error('Error editing business hours:', error);
        }
    };

    const updateBusinessHourName = (setName, newSetName) => {
        if (businessHours[setName]) {
            const updatedHours = businessHours[setName].map(hour => ({ ...hour, name: newSetName }));

            setBusinessHours(prevState => ({
                ...prevState,
                [newSetName]: updatedHours
            }));

            updatedHours.forEach(updatedHour => {
                api.put(`/all-business-hours/${updatedHour.id}`, { name: newSetName });
            });
        }
    };

    return (
        <Layout>
            {Object.keys(businessHours).map(timeframe => (
                <div style={{ padding: '1rem' }} key={timeframe}>
                    <Card className="mb-3">
                        <CardHeader tag="h3">{timeframe}</CardHeader>
                        <CardBody>
                            <Table striped hover>
                                <thead>
                                    <tr>
                                        <th>Day of Week</th>
                                        <th>Start Time</th>
                                        <th>End Time</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {businessHours[timeframe].map(hours => (
                                        <tr key={hours.id}>
                                            <td>{DayOfWeek[hours.day_of_week]}</td>
                                            <td>{new Date(`1970-01-01T${hours.start_time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</td>
                                            <td>{new Date(`1970-01-01T${hours.end_time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</td>
                                            <td>
                                                <Button color="primary" onClick={() => openEditModal(hours)}>
                                                    Edit
                                                </Button>
                                                {' '}
                                                <Button color="danger" onClick={() => deleteBusinessHours(hours.id)}>
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </div>
            ))}

            <div style={{ padding: '1rem' }}>
                <Button color="primary" onClick={() => setNewModalOpen(true)}>
                    Add Business Hours
                </Button>
            </div>

            {newModalOpen && (
                <NewBusinessHourModal
                    isOpen={newModalOpen}
                    toggle={() => setNewModalOpen(!newModalOpen)}
                    onSave={addBusinessHours}
                    businessHours={newBusinessHours}
                    timeframes={timeframes}
                    onInputChange={(name, value) => setNewBusinessHours(prevState => ({ ...prevState, [name]: value }))}
                />
            )}

            {editBusinessHours && editModalOpen && (
                <EditBusinessHourModal
                    isOpen={editModalOpen}
                    toggle={() => setEditModalOpen(!editModalOpen)}
                    onSave={editBusinessHoursData}
                    businessHours={editBusinessHours}
                    timeframes={timeframes}
                    onInputChange={(name, value) => setEditBusinessHours(prevState => ({ ...prevState, [name]: value }))}
                    onSetRename={(setName, newSetName) => updateBusinessHourName(setName, newSetName)}
                />
            )}
        </Layout>
    );
};

export default BusinessHours;